import { Controller, useForm, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useLocation, useNavigate } from 'react-router-dom'
import MaskedInput from 'react-text-mask'
import dayjs from 'dayjs'
import Button from 'components/common/Button/Button'
import Radio from 'components/common/Radio/Radio'
import Input from 'components/common/Input/Input'
import InputError from 'components/common/InputError/InputError'
import Title from 'components/common/PageTitle/PageTitle'
import { formParams, maskedDate } from 'constants/inputValidation'
import { next } from 'components/Freemium/Nav/Nav'
import style from './Children.module.scss'
import mainStyle from '../FreemiumComponents.module.scss'


const Children = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, pathname } = useLocation()
  const children = (state.client?.children || []).map(c => ({
    firstname: c.firstname || '',
    lastname: c.lastname || '',
    birthdate: maskedDate(c.birthdate, dayjs) || '',
    placeOfBirth: c.placeOfBirth || '',
    countryOfBirth: c.countryOfBirth || '',
    gender: c.gender || ''
  }))

  // Initialize form
  const required = t('common:form.incorrect')
  const {
    control,
    register,
    formState: { errors, isValid },
    handleSubmit,
    clearErrors
  } = useForm({
    ...formParams,
    values: {
      children
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'children'
  })


  // Submit form => Navigate to the next page
  const onSubmit = handleSubmit(async data => {
    next({
      pathname, navigate, state,
      data: { client: data }
    })
  })

  const add = () => {
    if (children.length >= 3) return
    append({
      firstname: '',
      lastname: '',
      birthdate: '',
      placeOfBirth: '',
      countryOfBirth: '',
      gender: ''
    })
    setTimeout(() => {
      clearErrors(`children`)
    }, 0)
  }

  const {
    title,
    intro,
    removeChild,
    child,
    addChild,
    boy,
    girl,
    ageRangeError
  } = state.contract.contractType.slice(-1)[0].wordings?.children || {
    title: 'title',
    intro: 'intro',
    removeChild: 'removeChild',
    child: 'child',
    addChild: 'addChild',
    boy: 'boy',
    girl: 'girl',
    ageRangeError: 'ageRangeError'
  }


  return (
    <>
      <Title className={mainStyle['page-title']} text={`${title} 👶`} />

      {!fields.length && <p>{intro}</p>}

      <form className={mainStyle.form} onSubmit={onSubmit}>
      {fields.map( (field, idx) => (
        <div className={classNames(style.form, mainStyle.form)} key={field.id}>
        <h3 className={style.subtitle}>{child} {idx+1} <span onClick={() => remove(idx)}>{removeChild}</span></h3>

          <Radio
            register={register(`children.${idx}.gender`, { required })}
            label={t('freemium:profil.civility')+' *'}
            values={[
              { text: boy, value: "mr" },
              { text: girl, value: "mme" }
            ]}
          />


          <Input
            error={errors.children?.[idx]?.firstname}
            register={register(`children.${idx}.firstname`, { required })}
            label={t('common:form.firstname.label')+'*'}
            placeholder={`${t('common:your')} ${t('common:form.firstname.label').toLowerCase()}`}
            allowOnly={/[\p{L} '-]+/gu}
            autocomplete="given-name"
          />
          <InputError error={errors.children?.[idx]?.firstname} />


          <Input
            error={errors.children?.[idx]?.lastname}
            register={register(`children.${idx}.lastname`, { required })}
            label={t('common:form.lastname.label')+'*'}
            placeholder={`${t('common:your')} ${t('common:form.lastname.label').toLowerCase()}`}
            allowOnly={/[\p{L} '-]+/gu}
            autocomplete="family-name"
          />
          <InputError error={errors.children?.[idx]?.lastname} />


          <div className="input-label">{t('common:form.birthdate.label')+'*'}</div>
          <Controller
            control={control}
            name={`children.${idx}.birthdate`}
            rules={{
                required: t('common:form.incorrect'),
                validate: {
                  format: value => /\d{2}\/\d{2}\/\d{4}/.test(value) || t('common:form.birthdate.errorFormat'),
                  range: value => (dayjs().isAfter(dayjs(value, 'DD/MM/YYYY', true).add(5, 'years').add(1, 'days')) && dayjs().isBefore(dayjs(value, 'DD/MM/YYYY', true).add(18, 'years'))) || ageRangeError
                }
              }}
            render={({ field: { onBlur, onChange, value } }) => (
              <MaskedInput
                className={classNames('masked-input', errors.birthdate && 'error')}
                mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                guide={false}
                keepCharPositions={false}
                onBlur={onBlur}
                onChange={event => onChange(event.target.value)}
                placeholder={t('common:form.birthdate.placeholder')}
                value={value}
              />
            )}
          />
          <InputError error={errors.children?.[idx]?.birthdate} />


          <Input
            error={errors.children?.[idx]?.placeOfBirth}
            register={register(`children.${idx}.placeOfBirth`, { required })}
            label={t('common:form.placeOfBirth.label')+'*'}
            placeholder={`${t('common:your')} ${t('common:form.placeOfBirth.label').toLowerCase()}`}
            allowOnly={/[a-zA-ZÀ-ÿ- ]/}
          />
          <InputError error={errors.children?.[idx]?.placeOfBirth} />


          <Input
            error={errors.children?.[idx]?.countryOfBirth}
            register={register(`children.${idx}.countryOfBirth`, { required })}
            label={t('common:form.countryOfBirth.label')+'*'}
            placeholder={`${t('common:your')} ${t('common:form.countryOfBirth.label').toLowerCase()}`}
            allowOnly={/[a-zA-ZÀ-ÿ- ]/}
          />
          <InputError error={errors.children?.[idx]?.countryOfBirth} />


        </div>
      ))}

      <footer className={style.footer}>
        {fields.length > 0 && <p className={mainStyle.required}>{t('common:form.mandatoryFields')}</p>}
        {fields.length < 3 && <Button
          className={mainStyle.button}
          label={addChild}
          onClick={add}
          type="primary"
        />}

        <Button
          className={mainStyle.button}
          label={t('common:next')}
          onClick={handleSubmit}
          disabled={!isValid}
          type="primary"
        />
      </footer>
      </form>
    </>
  )
}

export default Children
